/**
 * SDTM Mapping Panel
 *
 * Main panel for the SDTM Mapping Specification document. Displays the
 * 12 prose sections grouped by category, the SDTM domain inventory, and the
 * variable-level mapping table. Backed by the dataManagement/sdtmMapping
 * Firestore document and its sections / variables / domains subcollections.
 */

import { GENERATION_LOCK_STALE_MINUTES, GENERATION_LOCK_STALE_MS } from '../../../../functions/src/utils/generationWindow';
// The one reading of "did this mapping fail?", shared with every server-side
// reader so the panel's counts cannot disagree with the checks (2026-10-19).
import {
  isMappingFailedRow,
  isModelProposedNotSubmittedRow,
  mappingFailedLabel,
  MODEL_PROPOSAL_LABEL,
  needsMappingReview,
  panelMappingCounts,
} from '../../../../functions/src/sdtm/mappingFailure';
import { useState, useEffect } from 'react';
import SafeHtml from '../common/SafeHtml';
import { SectionStatusBadge, SectionFailureNotice } from '../common/SectionStatus';
import { compareSectionNumbers } from '../../features/documents/sectionOrder';
import { regenerateHint } from '../../features/documents/sectionStatus';
import { generationStatusNotice } from '../../features/documents/edtsDisplay';
import {
  onSnapshot,
} from 'firebase/firestore';
import { httpsCallable } from '../../lib/callable';
import { functions } from '../../firebase';
import { CLOUD_FUNCTIONS, CALLABLE_TIMEOUTS } from '../../constants/firebase';
import { dataManagementDoc, dataManagementSubcollection } from '../../constants/firestorePaths';
import { useToast } from '../../contexts/ToastContext';
import RenameNotice from '../../features/renames/RenameNotice';
import DownloadButton from '../../features/downloads/DownloadButton';
import { useDocumentDownload } from '../../features/downloads/useDocumentDownload';
import {
  studyDocumentDownloadOptions,
  SUPPQUAL_NOTHING_MESSAGE,
} from '../../features/downloads/studyDocumentDownloads';
import {
  Loader2,
  Play,
  RefreshCw,
  FileText,
  ClipboardList,
  ChevronDown,
  ChevronRight,
  Filter,
  BookOpen,
  AlertTriangle,
  ShieldCheck,
  GitMerge,
  Database,
  Layers,
  ListTree,
  Search,
} from 'lucide-react';
import { displayGenerationError } from '../../utils/generationFailureText';

/* ---------- Types ---------- */

interface SDTMVersionSelection {
  sdtmVersion: string;
  sdtmIGVersion: string;
  defineXMLVersion: string;
  ctVersion: string;
  catalogReference: string;
  resolvedFromStartDate: string | null;
}

interface SDTMDoc {
  version: number;
  status: string;
  title: string;
  generatedAt: any;
  generatedBy: string;
  studyInfo?: any;
  versions?: SDTMVersionSelection;
  domainCount?: number;
  variableCount?: number;
  unmappedCount?: number;
  customVariableCount?: number;
  /** Structural conformance summary written by the generator's gate. */
  conformance?: {
    checkedRows: number;
    errorRows: number;
    warningRows: number;
    cleanRows: number;
    byCode?: Record<string, number>;
  };
  _generating?: boolean;
  _generationStartedAt?: any;
  _generationStartedBy?: string;
  _generationType?: string;
  _generationError?: string;
  _generationFailedAt?: any;
  failedSectionCount?: number;
  generationStatus?: string;
}

interface SDTMSection {
  id: string;
  sectionId: string;
  number: string;
  title: string;
  group: string;
  content: string;
  status: string;
  placeholderCount?: number;
}

interface SDTMVariableMapping {
  id: string;
  formCode: string;
  formName?: string;
  fieldName: string;
  fieldLabel: string;
  domain: string;
  domainName?: string;
  variable: string;
  variableLabel?: string;
  dataType?: string;
  testCode?: string | null;
  testName?: string | null;
  origin?: string;
  codelistName?: string | null;
  scenario?: string;
  source?: 'lookup' | 'ai' | 'manual' | 'pre_filter';
  validationFlags?: string[];
  category?: string | null;
  notes?: string | null;
  /** Read so a failed mapping stored before 2026-10-19 (as not_submitted) is recognised. */
  rationale?: string;
}

interface SDTMDomainInventoryEntry {
  domainCode: string;
  domainName: string;
  observationClass: string;
  sourceForms: string[];
  variableCount: number;
  isStandard: boolean;
}

interface SDTMMappingPanelProps {
  projectId: string;
  /**
   * The Hub's in-flight state for this document, so ONE generation disables
   * EVERY control that can start it.
   *
   * This panel is rendered inside the Hub's sdtmMapping card, which carries its
   * own Regenerate button, so two controls for the same action sit on screen
   * together. Each used to hold only its own state. Both do read the server's
   * `_generating` marker, but that is set only after a round trip, so at the
   * instant of a click neither could see the other's — and on 2026-08-17 one
   * click on the MCS card produced two requests 0.67ms apart, the second
   * refused by the per-study lock and shown to the user as a failure while the
   * first was succeeding.
   *
   * The Hub owns the authoritative state (its synchronous `generatingDocs`
   * ORed with the same marker), so passing it down closes that window
   * immediately rather than a round trip later.
   */
  generating?: boolean;
}

/* ---------- Constants ---------- */

const GROUP_COLORS: Record<string, { bg: string; border: string; text: string; icon: React.ReactNode }> = {
  introduction:     { bg: 'bg-slate-50',   border: 'border-slate-200',   text: 'text-slate-700',   icon: <BookOpen className="h-4 w-4" /> },
  regulatory:       { bg: 'bg-rose-50',    border: 'border-rose-200',    text: 'text-rose-700',    icon: <ShieldCheck className="h-4 w-4" /> },
  mapping_overview: { bg: 'bg-cyan-50',    border: 'border-cyan-200',    text: 'text-cyan-700',    icon: <GitMerge className="h-4 w-4" /> },
  supplemental:     { bg: 'bg-violet-50',  border: 'border-violet-200',  text: 'text-violet-700',  icon: <Layers className="h-4 w-4" /> },
  timing:           { bg: 'bg-amber-50',   border: 'border-amber-200',   text: 'text-amber-700',   icon: <AlertTriangle className="h-4 w-4" /> },
  trial_design:     { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', icon: <ListTree className="h-4 w-4" /> },
  external_data:    { bg: 'bg-indigo-50',  border: 'border-indigo-200',  text: 'text-indigo-700',  icon: <Database className="h-4 w-4" /> },
  custom:           { bg: 'bg-orange-50',  border: 'border-orange-200',  text: 'text-orange-700',  icon: <FileText className="h-4 w-4" /> },
  sdrg:             { bg: 'bg-teal-50',    border: 'border-teal-200',    text: 'text-teal-700',    icon: <ClipboardList className="h-4 w-4" /> },
};

const STATUS_STYLES: Record<string, string> = {
  draft:       'bg-gray-100 text-gray-600',
  in_review:   'bg-blue-100 text-blue-700',
  approved:    'bg-green-100 text-green-700',
};

const SCENARIO_STYLES: Record<string, string> = {
  direct_carry:     'bg-green-50 text-green-700 border-green-200',
  rename:           'bg-blue-50 text-blue-700 border-blue-200',
  attribute_change: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  reformat:         'bg-violet-50 text-violet-700 border-violet-200',
  combine:          'bg-amber-50 text-amber-700 border-amber-200',
  split:            'bg-orange-50 text-orange-700 border-orange-200',
  derive:           'bg-rose-50 text-rose-700 border-rose-200',
  assign:           'bg-indigo-50 text-indigo-700 border-indigo-200',
  not_submitted:    'bg-gray-100 text-gray-500 border-gray-300',
  mapping_failed:   'bg-red-50 text-red-700 border-red-300',
};

const SOURCE_LABELS: Record<string, string> = {
  lookup: 'Library',
  ai:     'Mapped',
  manual: 'Manual',
};

/**
 * Plain-language wording for the conformance codes the generator stores on each
 * row. Mirrors CONFORMANCE_LABELS in functions/src/sdtm/conformance.ts — the two
 * cannot share a module across the web/functions boundary, so a code with no entry
 * here falls back to its raw form and is treated as a warning.
 */
const CONFORMANCE_LABELS: Record<string, string> = {
  variable_too_long: 'SDTM variable name is longer than 8 characters',
  testcd_too_long: 'Test code is longer than the 8-character SDTM limit',
  testcd_illegal_chars: 'Test code may contain only letters, digits and underscores',
  testcd_starts_with_digit: 'Test code may not start with a digit',
  testcd_not_uppercase: 'Test code is not uppercase',
  test_name_too_long: 'Test name exceeds the 40-character SDTM limit and will need shortening',
  findings_missing_testcd: 'Findings result row has no test code (required for this domain)',
  testcd_on_non_findings_domain: 'Test code on a domain that is not a findings domain — confirm the domain',
  ie_missing_iecat: 'Eligibility criterion has no INCLUSION/EXCLUSION category (required in IE)',
  ie_invalid_iecat: 'Eligibility category must be INCLUSION or EXCLUSION',
  invalid_observation_class: 'Observation class is not one of the SDTM classes',
  observation_class_domain_mismatch: 'Observation class contradicts the domain it is assigned to',
  duplicate_testcd_differing_test: 'Same test code used for differently named tests — confirm they are the same test',
  unmapped: 'Not mapped to SDTM',
  mapping_failed: 'SDTM mapping failed: this field still needs a mapping (it is not a not-submitted decision)',
  model_not_submitted_proposal: 'Not submitted only on the mapping model\'s proposal: confirm it or map the field',
};

/** Codes the gate raises as errors; everything else renders as a warning. */
const CONFORMANCE_ERRORS = new Set([
  'mapping_failed',
  'variable_too_long',
  'testcd_too_long',
  'testcd_illegal_chars',
  'testcd_starts_with_digit',
  'findings_missing_testcd',
  'ie_missing_iecat',
  'ie_invalid_iecat',
  'invalid_observation_class',
  'observation_class_domain_mismatch',
]);

/**
 * Documents generated before the gate stored value-suffixed codes
 * ("invalid_observation_class:Events"). Strip the suffix so those older rows still
 * get plain-language wording and the correct severity colour instead of leaking a
 * machine string to the customer.
 */
const baseCode = (flag: string): string => String(flag).split(':')[0];

const conformanceLabel = (code: string): string =>
  CONFORMANCE_LABELS[baseCode(code)] || baseCode(code).replace(/_/g, ' ');

const hasConformanceError = (v: { validationFlags?: string[] }): boolean =>
  (v.validationFlags || []).some((f) => CONFORMANCE_ERRORS.has(baseCode(f)));

/**
 * Needs Review: a conformance issue, a failed mapping (legacy rows, which carry
 * only the old "unmapped" flag, included) or a model not-submitted proposal.
 */
const needsReview = (v: { validationFlags?: string[] }): boolean =>
  hasConformanceIssue(v) || needsMappingReview(v as unknown as Record<string, unknown>);

const hasConformanceIssue = (v: { validationFlags?: string[] }): boolean =>
  (v.validationFlags || []).some((f) => baseCode(f) !== 'unmapped');

const PROGRESS_STEPS = [
  'Loading data dictionary and CRF forms...',
  'Resolving SDTM/SDTMIG versions from study start date...',
  'Phase 1: looking up CRF variables in CDASH→SDTM library...',
  'Phase 1: mapping unknown variables...',
  'Backfilling data dictionary with SDTM mappings...',
  'Phase 2: generating regulatory framework section...',
  'Phase 2: generating timing variable derivations...',
  'Phase 2: generating trial design domain narratives...',
  'Phase 2: generating reviewer guide notes...',
  'Finalizing document...',
];

const GROUP_LABELS: Record<string, string> = {
  introduction:     'Introduction',
  regulatory:       'Regulatory Framework',
  mapping_overview: 'Mapping Overview',
  supplemental:     'Supplemental Qualifiers & Codelists',
  timing:           'Timing Variables',
  trial_design:     'Trial Design Domains',
  external_data:    'External Data',
  custom:           'Custom Variables',
  sdrg:             "Reviewer's Guide",
};

const GROUP_ORDER = [
  'introduction',
  'regulatory',
  'mapping_overview',
  'supplemental',
  'timing',
  'trial_design',
  'external_data',
  'custom',
  'sdrg',
];

/* ---------- Helpers ---------- */

function groupSections(sections: SDTMSection[]): Record<string, SDTMSection[]> {
  const groups: Record<string, SDTMSection[]> = {};
  for (const s of sections) {
    const key = (s.group || 'other').toLowerCase();
    if (!groups[key]) groups[key] = [];
    groups[key].push(s);
  }
  for (const key of Object.keys(groups)) {
    groups[key].sort((a, b) => compareSectionNumbers(a.number, b.number));
  }
  return groups;
}

function getGroupStyle(group: string) {
  const key = group.toLowerCase();
  return GROUP_COLORS[key] || { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-700', icon: <FileText className="h-4 w-4" /> };
}

function formatGroupLabel(group: string): string {
  return GROUP_LABELS[group] || group
    .split(/[-_]/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function formatObservationClass(cls: string): string {
  return (cls || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/* ---------- Component ---------- */

export default function SDTMMappingPanel({ projectId, generating: hubGenerating = false }: SDTMMappingPanelProps) {
  const [sdtmDoc, setSdtmDoc] = useState<SDTMDoc | null>(null);
  const [sections, setSections] = useState<SDTMSection[]>([]);
  const [variables, setVariables] = useState<SDTMVariableMapping[]>([]);
  const [domains, setDomains] = useState<SDTMDomainInventoryEntry[]>([]);
  const [generating, setGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState('');
  const [expandedSectionId, setExpandedSectionId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'sections' | 'domains' | 'variables'>('sections');
  const [loading, setLoading] = useState(true);
  // Shares its running state with the card's Download button (same group).
  const download = useDocumentDownload(projectId, 'sdtmMapping');
  const [exportNotice, setExportNotice] = useState<string | null>(null);
  const { showToast } = useToast();
  const [domainFilter, setDomainFilter] = useState<string>('all');
  const [variableSearch, setVariableSearch] = useState<string>('');
  const [reviewOnly, setReviewOnly] = useState(false);
  const [dataDictExists, setDataDictExists] = useState<boolean | null>(null);

  const STALE_GENERATION_MS = GENERATION_LOCK_STALE_MS;

  const isGeneratingFromMarker = (() => {
    if (!sdtmDoc?._generating) return false;
    const startedAt = sdtmDoc._generationStartedAt;
    if (!startedAt?.toDate) return true;
    const startedMs = startedAt.toDate().getTime();
    return Date.now() - startedMs < STALE_GENERATION_MS;
  })();

  const isStaleGeneration = (() => {
    if (!sdtmDoc?._generating) return false;
    const startedAt = sdtmDoc._generationStartedAt;
    if (!startedAt?.toDate) return false;
    const startedMs = startedAt.toDate().getTime();
    return Date.now() - startedMs >= STALE_GENERATION_MS;
  })();

  // Folds in the Hub's in-flight state (the shared guard — see
  // SDTMMappingPanelProps.generating) alongside the local fast path and the Firestore
  // marker, so a click on the card's Regenerate disables this one at once.
  const showGenerating = generating || hubGenerating || (isGeneratingFromMarker && !isStaleGeneration);

  // Elapsed time counter for generation progress
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  useEffect(() => {
    if (!showGenerating) { setElapsedSeconds(0); return; }
    const startMs = generating
      ? Date.now()
      : sdtmDoc?._generationStartedAt?.toDate?.()?.getTime() || Date.now();
    setElapsedSeconds(Math.max(0, Math.floor((Date.now() - startMs) / 1000)));
    const timer = setInterval(() => {
      setElapsedSeconds(Math.max(0, Math.floor((Date.now() - startMs) / 1000)));
    }, 1000);
    return () => clearInterval(timer);
  }, [showGenerating, generating, sdtmDoc?._generationStartedAt]);

  /* --- Firestore: data dictionary exists check --- */
  useEffect(() => {
    if (!projectId) return;
    const unsub = onSnapshot(
      dataManagementDoc(projectId, 'dataDictionary'),
      (snap) => {
        const data = snap.data();
        setDataDictExists(snap.exists() && Array.isArray(data?.entries) && data.entries.length > 0);
      },
      () => setDataDictExists(false)
    );
    return () => unsub();
  }, [projectId]);

  /* --- Firestore: SDTM doc --- */
  useEffect(() => {
    if (!projectId) return;
    const unsubscribe = onSnapshot(
      dataManagementDoc(projectId, 'sdtmMapping'),
      (snapshot) => {
        if (snapshot.exists()) {
          setSdtmDoc(snapshot.data() as SDTMDoc);
        }
        setLoading(false);
      },
      (err) => {
        console.error('Error loading SDTM Mapping:', err);
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, [projectId]);

  /* --- Firestore: sections --- */
  useEffect(() => {
    if (!projectId || !sdtmDoc) return;
    const sectionsRef = dataManagementSubcollection(projectId, 'sdtmMapping', 'sections');
    const unsubscribe = onSnapshot(
      sectionsRef,
      (snapshot) => {
        const list: SDTMSection[] = snapshot.docs
          .map((d) => ({
            id: d.id,
            ...(d.data() as Omit<SDTMSection, 'id'>),
          }))
          .sort((a, b) => compareSectionNumbers(a.number, b.number));
        setSections(list);
      },
      (err) => {
        console.error('Error loading SDTM sections:', err);
      }
    );
    return () => unsubscribe();
  }, [projectId, sdtmDoc]);

  /* --- Firestore: variable mappings --- */
  useEffect(() => {
    if (!projectId || !sdtmDoc) return;
    const varsRef = dataManagementSubcollection(projectId, 'sdtmMapping', 'variables');
    const unsubscribe = onSnapshot(
      varsRef,
      (snapshot) => {
        const list: SDTMVariableMapping[] = snapshot.docs.map((d) => ({
          id: d.id,
          ...(d.data() as Omit<SDTMVariableMapping, 'id'>),
        }));
        list.sort((a, b) => {
          const dom = (a.domain || 'ZZZ').localeCompare(b.domain || 'ZZZ');
          if (dom !== 0) return dom;
          return (a.variable || '').localeCompare(b.variable || '');
        });
        setVariables(list);
      },
      (err) => {
        console.error('Error loading SDTM variables:', err);
      }
    );
    return () => unsubscribe();
  }, [projectId, sdtmDoc]);

  /* --- Firestore: domain inventory --- */
  useEffect(() => {
    if (!projectId || !sdtmDoc) return;
    const domainsRef = dataManagementSubcollection(projectId, 'sdtmMapping', 'domains');
    const unsubscribe = onSnapshot(
      domainsRef,
      (snapshot) => {
        const list: SDTMDomainInventoryEntry[] = snapshot.docs
          .map((d) => d.data() as SDTMDomainInventoryEntry)
          .sort((a, b) => a.domainCode.localeCompare(b.domainCode));
        setDomains(list);
      },
      (err) => {
        console.error('Error loading SDTM domains:', err);
      }
    );
    return () => unsubscribe();
  }, [projectId, sdtmDoc]);

  /* --- Generate --- */
  const handleGenerate = async () => {
    setGenerating(true);
    setGenerationProgress(PROGRESS_STEPS[0]);

    let step = 0;
    const progressTimer = setInterval(() => {
      step++;
      if (step < PROGRESS_STEPS.length) {
        setGenerationProgress(PROGRESS_STEPS[step]);
      }
    }, 15000);

    try {
      const generateFn = httpsCallable(functions, CLOUD_FUNCTIONS.GENERATE_SDTM_MAPPING_V2, { timeout: CALLABLE_TIMEOUTS.GENERATOR_XLARGE });
      await generateFn({ projectId });
    } catch (err: any) {
      console.error('SDTM Mapping generation error:', err);
      showToast(err.message || 'Failed to generate SDTM Mapping', 'error');
    } finally {
      clearInterval(progressTimer);
      setGenerating(false);
      setGenerationProgress('');
    }
  };

  /* --- Downloads ---
   * The same six options as the card's Download menu (features/downloads). The
   * SUPPQUAL download with no SUPP-- domains shows its notice in this panel. */
  const downloadOptions = studyDocumentDownloadOptions('sdtmMapping', {
    projectId,
    onSuppqualNothing: () => setExportNotice(SUPPQUAL_NOTHING_MESSAGE),
    // Every SDTM download clears the notice first, as the old buttons did.
    onDownloadStart: () => setExportNotice(null),
  });
  const acrfOption = downloadOptions.find((o) => o.request.id === 'sdtm:acrf');

  /* --- Derived data --- */
  const grouped = groupSections(sections);
  // Stored as incomplete when any prose section failed (docgen reasons, 2026-10-01).
  const incompleteNotice = sdtmDoc ? generationStatusNotice(sdtmDoc, 'Regenerate Entire Specification') : undefined;

  const orderedGroups = GROUP_ORDER.filter((g) => grouped[g] && grouped[g].length > 0);
  const otherGroups = Object.keys(grouped).filter((g) => !GROUP_ORDER.includes(g));
  const groupedKeys = [...orderedGroups, ...otherGroups];

  const filteredVariables = variables.filter((v) => {
    if (reviewOnly && !needsReview(v)) return false;
    if (domainFilter !== 'all' && v.domain !== domainFilter) return false;
    if (variableSearch) {
      const q = variableSearch.toLowerCase();
      if (
        !v.fieldName?.toLowerCase().includes(q) &&
        !v.variable?.toLowerCase().includes(q) &&
        !(v.fieldLabel || '').toLowerCase().includes(q)
      ) {
        return false;
      }
    }
    return true;
  });

  // Conformance counts derived from the rows themselves, so the review surface
  // stays correct for documents generated before the gate existed (which have no
  // summary on the parent doc) and after an in-place row edit.
  const conformanceErrorCount = variables.filter(hasConformanceError).length;
  const conformanceIssueCount = variables.filter(needsReview).length;

  // Each row counted once, in exactly one tile, so the tiles add up to the
  // total (panelMappingCounts, tested in functions/src/sdtm).
  const tiles = panelMappingCounts(variables as unknown as Record<string, unknown>[]);
  const lookupCount = tiles.library;
  const preFilteredCount = tiles.skipped;
  // Failed rows carry source 'ai' too, so "Mapped (Fallback)" counts only rows
  // the fallback actually mapped. Not-submitted and failed are counted apart.
  const aiCount = tiles.fallback;
  const lookupPct = variables.length > 0 ? Math.round((lookupCount / variables.length) * 100) : 0;

  const statusLabel = sdtmDoc?.status || 'draft';
  const statusStyle = STATUS_STYLES[statusLabel] || STATUS_STYLES.draft;

  /* --- Loading state --- */
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  /* --- Not yet generated --- */
  const hasRealContent = !!(sdtmDoc && (sdtmDoc.version || sdtmDoc.title));
  if (!sdtmDoc || !hasRealContent) {
    return (
      <div className="space-y-4">
        <div className="text-center py-8">
          <p className="text-gray-500 mb-2">
            Generate an SDTM Mapping Specification covering CDISC SDTM domain assignments,
            variable-level mappings, controlled terminology, timing variables, trial design domains,
            and regulatory submission readiness.
          </p>
          <p className="text-xs text-gray-400 mb-6">
            Content is generated from your data dictionary, CRFs, protocol facts, and the
            FDA Data Standards Catalog. Each variable mapping includes the SDTM domain,
            variable name, controlled terminology codelist, origin, derivation algorithm,
            and one of nine mapping scenarios. Generated mappings are written back to your
            data dictionary and immediately improve the next aCRF regeneration.
          </p>


          {sdtmDoc?._generationError && !sdtmDoc?._generating && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-2 mb-3">
              <strong>Previous generation failed:</strong> {displayGenerationError(sdtmDoc._generationError)}
            </div>
          )}

          {isStaleGeneration && (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2 mb-3">
              A previous generation appears to have stalled (started more than {GENERATION_LOCK_STALE_MINUTES} minutes ago).
              You can try generating again.
            </div>
          )}

          {!dataDictExists && dataDictExists !== null && (
            <div className="mb-4 text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-lg p-3">
              Generate the Data Dictionary first. The SDTM Mapping Specification reads CRF
              variables from the data dictionary and writes the mappings back to it.
            </div>
          )}

          {showGenerating ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-gray-700">
                  {generating ? 'Generating...' : 'Generation in progress in the background...'}
                </p>
                <span className="text-xs text-gray-400 tabular-nums">
                  {Math.floor(elapsedSeconds / 60)}:{String(elapsedSeconds % 60).padStart(2, '0')}
                </span>
              </div>
              <div className="flex items-center justify-center gap-2 text-sm text-blue-600">
                <Loader2 className="h-4 w-4 animate-spin" />
                {generating ? (generationProgress || 'Generating...') : 'Please wait...'}
              </div>
              <div className="max-w-xs mx-auto">
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-500 rounded-full animate-pulse w-2/3" />
                </div>
              </div>
              <p className="text-xs text-gray-400">
                This may take 5-8 minutes as each variable is mapped and each section is generated.
              </p>
            </div>
          ) : (
            dataDictExists && (
              <button
                onClick={handleGenerate}
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
              >
                <Play className="h-4 w-4" />
                Generate SDTM Mapping Specification
              </button>
            )
          )}
        </div>
      </div>
    );
  }

  /* --- Full UI --- */
  return (
    <div className="space-y-3">
      {/* Header bar */}
      <div className="bg-white border border-gray-200 rounded-lg px-4 py-3 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="text-lg font-semibold text-gray-800">SDTM Mapping Specification</h2>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusStyle}`}>
              {statusLabel === 'in_review'
                ? 'In Review'
                : statusLabel.charAt(0).toUpperCase() + statusLabel.slice(1)}
            </span>
            {sdtmDoc.versions && (
              <span className="text-xs text-gray-500 ml-1">
                SDTM v{sdtmDoc.versions.sdtmVersion} · SDTMIG v{sdtmDoc.versions.sdtmIGVersion} · Define-XML v{sdtmDoc.versions.defineXMLVersion} · CT {sdtmDoc.versions.ctVersion}
              </span>
            )}
          </div>
        </div>

        {/* Submission package downloads: one Download menu, as on the card */}
        <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-gray-100">
          <DownloadButton
            download={download}
            options={downloadOptions}
            variant="secondary"
            size="sm"
          />
        </div>
      </div>

      {/* The annotated CRF is generated here (Download Annotated CRF regenerates it), so its rename notice sits here too (plan step 2). */}
      {projectId && (
        <RenameNotice
          projectId={projectId}
          documentPath="dataManagement/acrf"
          documentLabel="Annotated CRF"
          onRegenerate={() => {
            if (acrfOption) void download.run(acrfOption.request);
          }}
          regenerating={download.runningId === 'sdtm:acrf'}
        />
      )}

      {/* Metric strip */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
        <div className="bg-white border border-gray-200 rounded-lg p-2.5">
          <div className="text-[10px] uppercase tracking-wider text-gray-400">Variables</div>
          <div className="text-xl font-semibold text-gray-800">{variables.length}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-2.5">
          <div className="text-[10px] uppercase tracking-wider text-gray-400">Domains</div>
          <div className="text-xl font-semibold text-gray-800">{domains.length}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-2.5">
          <div className="text-[10px] uppercase tracking-wider text-gray-400">Library Hits</div>
          <div className="text-xl font-semibold text-emerald-600">
            {lookupCount}
            <span className="text-xs text-gray-400 font-normal ml-1">({lookupPct}%)</span>
          </div>
        </div>
        {preFilteredCount > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg p-2.5">
            <div className="text-[10px] uppercase tracking-wider text-gray-400">Skipped (Non-SDTM)</div>
            <div className="text-xl font-semibold text-gray-400">{preFilteredCount}</div>
          </div>
        )}
        <div className="bg-white border border-gray-200 rounded-lg p-2.5">
          <div className="text-[10px] uppercase tracking-wider text-gray-400">Mapped (Fallback)</div>
          <div className="text-xl font-semibold text-blue-600">{aiCount}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-2.5">
          <div className="text-[10px] uppercase tracking-wider text-gray-400">Not Submitted</div>
          <div className="text-xl font-semibold text-gray-500">
            {tiles.notSubmitted}
          </div>
        </div>
        {tiles.modelProposals > 0 && (
          <div
            className="bg-white border border-amber-300 rounded-lg p-2.5"
            title="The mapping model proposed leaving these fields out. That is not a decision: confirm it or map the field."
          >
            <div className="text-[10px] uppercase tracking-wider text-gray-400">Proposed Not Submitted</div>
            <div className="text-xl font-semibold text-amber-600">{tiles.modelProposals}</div>
          </div>
        )}
        {tiles.failed > 0 && (
          <div
            className="bg-white border border-red-300 rounded-lg p-2.5"
            title="The mapping produced no usable answer for these fields. They still need an SDTM mapping; they are not deliberately left out."
          >
            <div className="text-[10px] uppercase tracking-wider text-gray-400">Failed to Map</div>
            <div className="text-xl font-semibold text-red-600">{tiles.failed}</div>
          </div>
        )}
        {conformanceIssueCount > 0 && (
          <button
            type="button"
            onClick={() => { setReviewOnly(true); setActiveTab('variables'); }}
            title="Show only the rows that need review"
            className={`text-left bg-white border rounded-lg p-2.5 transition-colors hover:bg-gray-50 ${
              conformanceErrorCount > 0 ? 'border-red-300' : 'border-amber-300'
            }`}
          >
            <div className="text-[10px] uppercase tracking-wider text-gray-400">Needs Review</div>
            <div className={`text-xl font-semibold ${conformanceErrorCount > 0 ? 'text-red-600' : 'text-amber-600'}`}>
              {conformanceIssueCount}
            </div>
          </button>
        )}
      </div>

      {/* Conformance disclosure — a structurally incomplete row must never look as
          finished as a correct one. Counts rows, not problems, so the number means
          "rows a person needs to look at". */}
      {conformanceIssueCount > 0 && (
        <div
          className={`rounded-lg border p-3 text-xs ${
            conformanceErrorCount > 0
              ? 'bg-red-50 border-red-200 text-red-800'
              : 'bg-amber-50 border-amber-200 text-amber-800'
          }`}
        >
          <div className="font-semibold">
            {conformanceErrorCount > 0
              ? `${conformanceErrorCount} row${conformanceErrorCount === 1 ? '' : 's'} need${conformanceErrorCount === 1 ? 's' : ''} correction before submission`
              : `${conformanceIssueCount} row${conformanceIssueCount === 1 ? '' : 's'} need${conformanceIssueCount === 1 ? 's' : ''} a second look`}
          </div>
          <p className="mt-1 leading-relaxed">
            The specification generated successfully. These rows did not pass the SDTM
            structure checks — for example a required test code left blank, or a name over
            the CDISC length limit.
            {conformanceErrorCount > 0 && conformanceIssueCount > conformanceErrorCount && (
              <> {conformanceIssueCount - conformanceErrorCount} further row
                {conformanceIssueCount - conformanceErrorCount === 1 ? ' is' : 's are'} flagged for
                confirmation only and may be correct as they stand.</>
            )}
          </p>
          <button
            type="button"
            onClick={() => { setReviewOnly(true); setActiveTab('variables'); }}
            className="mt-2 underline font-medium hover:no-underline"
          >
            Review these rows
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex bg-gray-100 rounded-lg p-0.5 w-fit">
        <button
          onClick={() => setActiveTab('sections')}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
            activeTab === 'sections'
              ? 'bg-white text-gray-800 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <FileText className="h-3.5 w-3.5" />
          Sections
          {sections.length > 0 && (
            <span className="ml-1 text-[10px] bg-indigo-100 text-indigo-600 px-1.5 py-0.5 rounded-full">
              {sections.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('domains')}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
            activeTab === 'domains'
              ? 'bg-white text-gray-800 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Layers className="h-3.5 w-3.5" />
          Domain Inventory
          {domains.length > 0 && (
            <span className="ml-1 text-[10px] bg-indigo-100 text-indigo-600 px-1.5 py-0.5 rounded-full">
              {domains.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('variables')}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
            activeTab === 'variables'
              ? 'bg-white text-gray-800 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <ClipboardList className="h-3.5 w-3.5" />
          Variable Mapping Table
          {variables.length > 0 && (
            <span className="ml-1 text-[10px] bg-indigo-100 text-indigo-600 px-1.5 py-0.5 rounded-full">
              {variables.length}
            </span>
          )}
        </button>
      </div>

      {exportNotice && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
          {exportNotice}
          <button onClick={() => setExportNotice(null)} className="ml-2 underline">
            dismiss
          </button>
        </div>
      )}

      {sdtmDoc?._generationError && !sdtmDoc?._generating && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-2">
          <strong>Previous generation failed:</strong> {displayGenerationError(sdtmDoc._generationError)}
        </div>
      )}

      {/* Some prose sections could not be generated */}
      {incompleteNotice && !showGenerating && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-2">
          {incompleteNotice}
        </div>
      )}

      {isStaleGeneration && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
          A previous generation appears to have stalled (started more than {GENERATION_LOCK_STALE_MINUTES} minutes ago).
          You can try generating again.
        </div>
      )}

      {showGenerating && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center justify-between mb-1">
            <p className="text-sm font-medium text-blue-700">
              {generating ? 'Generating...' : 'Generation in progress in the background...'}
            </p>
            <span className="text-xs text-blue-400 tabular-nums">
              {Math.floor(elapsedSeconds / 60)}:{String(elapsedSeconds % 60).padStart(2, '0')}
            </span>
          </div>
          <div className="flex items-center gap-2 text-sm text-blue-700">
            <Loader2 className="h-4 w-4 animate-spin" />
            {generating ? (generationProgress || 'Generating...') : 'Please wait...'}
          </div>
          <div className="mt-2 max-w-xs">
            <div className="h-2 bg-blue-100 rounded-full overflow-hidden">
              <div className="h-full bg-blue-500 rounded-full animate-pulse w-2/3" />
            </div>
          </div>
        </div>
      )}

      <div>

      {/* Sections tab */}
      {activeTab === 'sections' && (
        <div className="space-y-4">
          {groupedKeys.map((group) => {
            const groupSecs = grouped[group];
            const style = getGroupStyle(group);
            return (
              <div key={group} className={`border ${style.border} rounded-lg overflow-hidden`}>
                <div className={`${style.bg} px-4 py-2.5 flex items-center gap-2`}>
                  <span className={style.text}>{style.icon}</span>
                  <h3 className={`text-sm font-semibold ${style.text}`}>
                    {formatGroupLabel(group)}
                  </h3>
                  <span className="text-xs text-gray-400 ml-auto">
                    {groupSecs.length} section{groupSecs.length !== 1 ? 's' : ''}
                  </span>
                </div>

                <div className="divide-y divide-gray-100 bg-white">
                  {groupSecs.map((section) => {
                    const isExpanded = expandedSectionId === section.id;
                    return (
                      <div key={section.id}>
                        <button
                          onClick={() =>
                            setExpandedSectionId(isExpanded ? null : section.id)
                          }
                          className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                        >
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
                          )}
                          <span className="text-xs font-mono text-gray-400 w-8">
                            {section.number}
                          </span>
                          <span className="text-sm font-medium text-gray-800 flex-1">
                            {section.title}
                          </span>
                          <SectionStatusBadge section={section} />
                          {(section.placeholderCount ?? 0) > 0 && (
                            <span className="text-[10px] text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full">
                              {section.placeholderCount} placeholder{section.placeholderCount !== 1 ? 's' : ''}
                            </span>
                          )}
                        </button>

                        <SectionFailureNotice section={section} regenerateHint={regenerateHint('Regenerate Entire Specification')} />

                        {isExpanded && section.content && (
                          <div className="px-4 pb-4 pt-1 ml-11">
                            <SafeHtml
                              className="prose prose-sm max-w-none text-gray-700 bg-gray-50 border border-gray-200 rounded-lg p-4 max-h-[60vh] overflow-y-auto"
                              html={section.content}
                            />
                          </div>
                        )}
                        {isExpanded && !section.content && (
                          <div className="px-4 pb-4 pt-1 ml-11">
                            <p className="text-sm text-gray-400 italic">No content generated yet.</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {sections.length === 0 && !showGenerating && (
            <div className="text-center py-8 text-gray-400 text-sm">
              No sections found. Generate the SDTM Mapping to create sections.
            </div>
          )}
        </div>
      )}

      {/* Domain Inventory tab */}
      {activeTab === 'domains' && (
        <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Domain</th>
                  <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Observation Class</th>
                  <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Source CRFs</th>
                  <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Variables</th>
                  <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Type</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {domains.map((d) => (
                  <tr key={d.domainCode} className="hover:bg-gray-50 transition-colors align-top">
                    <td className="px-3 py-2 text-xs font-mono font-semibold text-gray-700">{d.domainCode}</td>
                    <td className="px-3 py-2 text-xs text-gray-700">{d.domainName}</td>
                    <td className="px-3 py-2 text-xs text-gray-600">{formatObservationClass(d.observationClass)}</td>
                    <td className="px-3 py-2 text-xs text-gray-600">{(d.sourceForms || []).join(', ') || '-'}</td>
                    <td className="px-3 py-2 text-xs font-mono text-gray-700">{d.variableCount}</td>
                    <td className="px-3 py-2 text-xs text-gray-500">{d.isStandard ? 'Standard' : 'Sponsor-Defined'}</td>
                  </tr>
                ))}
                {domains.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-400">
                      No domains populated yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Variable Mapping Table tab */}
      {activeTab === 'variables' && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Filter className="h-4 w-4 text-gray-400" />
            <select
              value={domainFilter}
              onChange={(e) => setDomainFilter(e.target.value)}
              className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 text-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-200"
            >
              <option value="all">All Domains</option>
              {domains.map((d) => (
                <option key={d.domainCode} value={d.domainCode}>
                  {d.domainCode} ({d.variableCount})
                </option>
              ))}
            </select>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
              <input
                type="text"
                value={variableSearch}
                onChange={(e) => setVariableSearch(e.target.value)}
                placeholder="Search by field name, variable, or label..."
                className="text-xs border border-gray-200 rounded-lg pl-7 pr-3 py-1.5 text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-200 w-72"
              />
            </div>
            {conformanceIssueCount > 0 && (
              <label
                className={`flex items-center gap-1.5 text-xs px-2 py-1.5 rounded-lg border cursor-pointer select-none ${
                  reviewOnly
                    ? 'bg-red-50 border-red-300 text-red-700'
                    : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                <input
                  type="checkbox"
                  checked={reviewOnly}
                  onChange={(e) => setReviewOnly(e.target.checked)}
                  className="h-3 w-3 accent-red-600"
                />
                Needs review only ({conformanceIssueCount})
              </label>
            )}
            <span className="text-xs text-gray-400 ml-auto">
              {filteredVariables.length} variable{filteredVariables.length !== 1 ? 's' : ''}
            </span>
          </div>

          <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Form</th>
                    <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Field</th>
                    <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Field Label</th>
                    <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Domain</th>
                    <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">SDTM Variable</th>
                    <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Origin</th>
                    <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Codelist</th>
                    <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Scenario</th>
                    <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Source</th>
                    <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Flags</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredVariables.map((v) => (
                    <tr key={v.id} className="hover:bg-gray-50 transition-colors align-top">
                      <td className="px-3 py-2 text-xs font-mono text-gray-600 whitespace-nowrap">
                        {v.formCode || '-'}
                      </td>
                      <td className="px-3 py-2 text-xs font-mono text-gray-700 whitespace-nowrap">
                        {v.fieldName || '-'}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-600 max-w-xs">
                        {v.fieldLabel || '-'}
                      </td>
                      <td className="px-3 py-2 text-xs font-mono font-semibold text-gray-700 whitespace-nowrap">
                        {v.domain || '-'}
                      </td>
                      <td className="px-3 py-2 text-xs font-mono text-gray-700 whitespace-nowrap">
                        {v.variable || '-'}
                        {v.testCode && (
                          <div className="text-[10px] text-gray-400 normal-case font-sans">TESTCD={v.testCode}</div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-600 whitespace-nowrap">
                        {isMappingFailedRow(v as unknown as Record<string, unknown>) ? '-' : v.origin || '-'}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">
                        {v.codelistName || '-'}
                      </td>
                      <td className="px-3 py-2">
                        {isMappingFailedRow(v as unknown as Record<string, unknown>) ? (
                          <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${SCENARIO_STYLES.mapping_failed}`}>
                            {mappingFailedLabel(v as unknown as Record<string, unknown>)}
                          </span>
                        ) : isModelProposedNotSubmittedRow(v as unknown as Record<string, unknown>) ? (
                          <span className="text-[10px] font-medium px-2 py-0.5 rounded-full border bg-amber-50 text-amber-700 border-amber-300">
                            {MODEL_PROPOSAL_LABEL}
                          </span>
                        ) : v.scenario && (
                          <span
                            className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${
                              SCENARIO_STYLES[v.scenario] || 'bg-gray-50 text-gray-600 border-gray-200'
                            }`}
                          >
                            {v.scenario.replace(/_/g, ' ')}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-[10px] text-gray-500 whitespace-nowrap">
                        {v.source ? SOURCE_LABELS[v.source] || v.source : '-'}
                      </td>
                      <td className="px-3 py-2">
                        {v.validationFlags && v.validationFlags.length > 0 && (
                          <div className="flex flex-col gap-1">
                            {v.validationFlags.map((f) => {
                              const isError = CONFORMANCE_ERRORS.has(baseCode(f));
                              return (
                                <span
                                  key={f}
                                  title={conformanceLabel(f)}
                                  className={`text-[10px] px-1.5 py-0.5 rounded border ${
                                    isError
                                      ? 'text-red-700 bg-red-50 border-red-200'
                                      : 'text-amber-700 bg-amber-50 border-amber-200'
                                  }`}
                                >
                                  {conformanceLabel(f)}
                                </span>
                              );
                            })}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                  {filteredVariables.length === 0 && (
                    <tr>
                      <td colSpan={10} className="px-4 py-8 text-center text-sm text-gray-400">
                        No variables match the current filter.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      </div>

      {/* Regenerate option */}
      <div className="flex justify-end">
        <button
          onClick={() => {
            if (
              confirm(
                'Regenerate the entire SDTM Mapping Specification with the latest data dictionary? Existing content will be overwritten and the data dictionary will be backfilled with new SDTM mappings.'
              )
            ) {
              handleGenerate();
            }
          }}
          disabled={showGenerating}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${showGenerating ? 'animate-spin' : ''}`} />
          Regenerate Entire Specification
        </button>
      </div>
    </div>
  );
}
